import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Check, Download, Clock, Loader2 } from "lucide-react";
import { useTheme } from "@/lib/theme";

// Client-facing per-deliverable stages — must mirror backend DELIVERABLE_STAGES.
export const DELIVERABLE_STAGES = [
  "Order Received",
  "Assets Downloaded",
  "Editor Assigned",
  "Editing",
  "Quality Check",
  "Delivered",
];

const ACCENT = "#6589ff";

// Bucket a stage into a coarse status for the row badge + top counters.
function statusBucket(stage) {
  if (stage === "Delivered") return "delivered";
  if (stage === "Order Received") return "waiting";
  return "in_progress";
}

// Theme tokens — light and dark. Used everywhere in the tracker so both modes
// look intentional (not just the dark palette with white-mode CSS overrides
// bleeding through inline styles).
function useTokens() {
  const { theme } = useTheme();
  const isLight = theme === "light";
  if (isLight) {
    return {
      isLight: true,
      cardBg: "#ffffff",
      cardBorder: "#e5e5ea",
      rowBg: "#ffffff",
      rowBorder: "#e5e5ea",
      rowHover: "#f5f5f7",
      rowInnerBg: "#fafafa",
      text: "#0a0a0a",
      textMuted: "#6a6a70",
      textDim: "#9a9a9f",
      progressTrack: "#eef0f4",
      helperText: "#6a6a70",
      // Chip tones
      chipNeutral: { bg: "#f5f5f7", border: "#e5e5ea", text: "#0a0a0a" },
      chipMuted:   { bg: "#f5f5f7", border: "#e5e5ea", text: "#6a6a70" },
      chipGreen:   { bg: "rgba(34,197,94,0.10)",  border: "rgba(34,197,94,0.35)", text: "#16a34a" },
      chipAccent:  { bg: "rgba(101,137,255,0.12)", border: "rgba(101,137,255,0.4)", text: "#4b6def" },
      chipAmber:   { bg: "rgba(234,179,8,0.14)",  border: "rgba(234,179,8,0.4)",  text: "#a16207" },
      // Marker tokens
      markerTodo:  { bg: "transparent", border: "1.5px solid #cfd0d5" },
      markerDone:  { bg: "#22c55e", checkColor: "#ffffff" },
      // Download btn — accent blue in both themes, but white text for readability
      downloadText: "#ffffff",
    };
  }
  return {
    isLight: false,
    cardBg: "#111",
    cardBorder: "#2b2b2b",
    rowBg: "#0d0d0d",
    rowBorder: "#2b2b2b",
    rowHover: "#131313",
    rowInnerBg: "#111",
    text: "#ffffff",
    textMuted: "#a0a0a0",
    textDim: "#7a7a7a",
    progressTrack: "#1a1a1a",
    helperText: "#7a7a7a",
    chipNeutral: { bg: "#0d0d0d", border: "#2b2b2b", text: "#ffffff" },
    chipMuted:   { bg: "#0d0d0d", border: "#2b2b2b", text: "#a0a0a0" },
    chipGreen:   { bg: "rgba(34,197,94,0.10)",  border: "rgba(34,197,94,0.32)", text: "#22c55e" },
    chipAccent:  { bg: "rgba(101,137,255,0.14)", border: "rgba(101,137,255,0.4)", text: ACCENT },
    chipAmber:   { bg: "rgba(234,179,8,0.12)",  border: "rgba(234,179,8,0.35)", text: "#eab308" },
    markerTodo:  { bg: "transparent", border: "1.5px solid #2b2b2b" },
    markerDone:  { bg: "#22c55e", checkColor: "#0a0a0a" },
    downloadText: "#0a0a0a",
  };
}

function StageBadge({ stage, t }) {
  const bucket = statusBucket(stage);
  const cfg =
    bucket === "delivered" ? { ...t.chipGreen, icon: <Check size={11} strokeWidth={3} />, label: "Delivered" }
    : bucket === "waiting" ? { ...t.chipMuted, icon: <Clock size={11} />, label: "Waiting" }
    : stage === "Quality Check"
      ? { ...t.chipAmber, icon: <Loader2 size={11} className="animate-spin" />, label: stage }
      : { ...t.chipAccent, icon: <Loader2 size={11} className="animate-spin" />, label: stage };

  return (
    <span
      className="inline-flex items-center gap-1.5 h-6 px-2.5 squircle-sm text-[11px] font-medium"
      style={{ background: cfg.bg, color: cfg.text, border: `1px solid ${cfg.border}` }}
    >
      {cfg.icon}
      {cfg.label}
    </span>
  );
}

function StageMarker({ state, t }) {
  if (state === "done") {
    return (
      <div
        className="h-5 w-5 rounded-full flex items-center justify-center flex-shrink-0"
        style={{ background: t.markerDone.bg }}
      >
        <Check size={11} strokeWidth={3} color={t.markerDone.checkColor} />
      </div>
    );
  }
  if (state === "current") {
    return (
      <div className="h-5 w-5 rounded-full flex items-center justify-center flex-shrink-0 relative">
        <div
          className="h-5 w-5 rounded-full absolute animate-ping opacity-40"
          style={{ background: ACCENT }}
        />
        <div
          className="h-2.5 w-2.5 rounded-full relative"
          style={{ background: ACCENT, boxShadow: `0 0 0 3px rgba(101,137,255,0.25)` }}
        />
      </div>
    );
  }
  return (
    <div
      className="h-5 w-5 rounded-full flex-shrink-0"
      style={{ background: t.markerTodo.bg, border: t.markerTodo.border }}
    />
  );
}

function DeliverableRow({ d, defaultOpen = false, t }) {
  const [open, setOpen] = useState(defaultOpen);
  const [hover, setHover] = useState(false);
  const currentIx = DELIVERABLE_STAGES.indexOf(d.stage);
  const bucket = statusBucket(d.stage);

  // Leading circle style based on state
  const leadingStyle =
    bucket === "delivered"
      ? { background: t.chipGreen.bg, border: `1px solid ${t.chipGreen.border}` }
      : bucket === "in_progress"
        ? d.stage === "Quality Check"
          ? { background: t.chipAmber.bg, border: `1px solid ${t.chipAmber.border}` }
          : { background: t.chipAccent.bg, border: `1px solid ${t.chipAccent.border}` }
        : { background: "transparent", border: `1.5px solid ${t.markerTodo.border.replace('1.5px solid ', '')}` };

  return (
    <div
      data-testid={`deliverable-row-${d.id}`}
      className="squircle-sm overflow-hidden"
      style={{ background: t.rowBg, border: `1px solid ${t.rowBorder}` }}
    >
      <button
        onClick={() => setOpen((v) => !v)}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        data-testid={`deliverable-toggle-${d.id}`}
        className="w-full flex items-center gap-4 px-4 sm:px-5 h-14 btn-anim text-left"
        style={{ background: hover ? t.rowHover : "transparent", color: t.text }}
      >
        {/* leading state icon */}
        <div className="flex-shrink-0">
          <div className="h-7 w-7 rounded-full flex items-center justify-center" style={leadingStyle}>
            {bucket === "delivered" ? (
              <Check size={13} strokeWidth={3} color={t.chipGreen.text} />
            ) : bucket === "in_progress" ? (
              <div
                className="h-2 w-2 rounded-full"
                style={{ background: d.stage === "Quality Check" ? t.chipAmber.text : ACCENT }}
              />
            ) : null}
          </div>
        </div>

        {/* title */}
        <div className="min-w-0 flex-1">
          <div
            className="text-sm font-medium truncate"
            data-testid={`deliverable-title-${d.id}`}
            style={{ color: t.text }}
          >
            {d.title}
          </div>
        </div>

        {/* badge + chevron */}
        <div className="flex items-center gap-3 flex-shrink-0">
          <StageBadge stage={d.stage} t={t} />
          <ChevronDown
            size={16}
            style={{
              color: t.textDim,
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s ease",
            }}
          />
        </div>
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div
              className="px-5 pb-5 pt-1"
              style={{ borderTop: `1px solid ${t.rowBorder}` }}
            >
              <ol className="mt-4 space-y-3">
                {DELIVERABLE_STAGES.map((s, i) => {
                  const state =
                    bucket === "delivered"
                      ? "done"
                      : i < currentIx
                        ? "done"
                        : i === currentIx
                          ? "current"
                          : "todo";
                  return (
                    <li
                      key={s}
                      className="flex items-center gap-3"
                      data-testid={`deliverable-${d.id}-stage-${i}`}
                    >
                      <StageMarker state={state} t={t} />
                      <div
                        className="text-sm"
                        style={{
                          color:
                            state === "todo"
                              ? t.textDim
                              : state === "current"
                                ? t.text
                                : t.textMuted,
                          fontWeight: state === "current" ? 500 : 400,
                        }}
                      >
                        {s}
                      </div>
                    </li>
                  );
                })}
              </ol>

              {bucket === "delivered" && d.url && (
                <a
                  href={d.url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`deliverable-download-${d.id}`}
                  className="mt-5 inline-flex items-center gap-2 h-9 px-4 squircle-sm text-sm font-medium btn-anim"
                  style={{ background: ACCENT, color: t.downloadText }}
                >
                  <Download size={14} /> Download
                </a>
              )}
              {bucket === "delivered" && !d.url && (
                <div className="mt-5 text-xs" style={{ color: t.helperText }}>
                  Marked delivered — file link coming from your editor shortly.
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function ProjectTracker({ order }) {
  const t = useTokens();
  const items = useMemo(
    () => Array.isArray(order?.project_deliverables) ? order.project_deliverables : [],
    [order],
  );

  const total = items.length;
  const completed = items.filter((d) => statusBucket(d.stage) === "delivered").length;
  const inProgress = items.filter((d) => statusBucket(d.stage) === "in_progress").length;
  const waiting = items.filter((d) => statusBucket(d.stage) === "waiting").length;
  const pct = total ? Math.round((completed / total) * 100) : 0;

  if (!total) {
    return (
      <div
        className="squircle p-8 text-sm"
        style={{ background: t.cardBg, border: `1px solid ${t.cardBorder}`, color: t.textMuted }}
      >
        No deliverables scheduled yet.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Overall Project Progress */}
      <div
        className="squircle p-6 sm:p-8"
        style={{ background: t.cardBg, border: `1px solid ${t.cardBorder}`, color: t.text }}
        data-testid="project-progress-card"
      >
        <div className="flex items-baseline justify-between gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-widest" style={{ color: t.textMuted }}>
              Overall project progress
            </div>
            <div className="mt-1 text-3xl sm:text-4xl font-bold tracking-tighter">
              {pct}%
              <span className="text-base font-medium tracking-normal ml-2" style={{ color: t.textMuted }}>
                complete
              </span>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-widest" style={{ color: t.textMuted }}>
              Delivered
            </div>
            <div className="mt-1 text-lg font-semibold" data-testid="progress-completed-frac">
              {completed}<span style={{ color: t.textDim, fontWeight: 400 }}>/{total}</span>
            </div>
          </div>
        </div>

        {/* Progress bar */}
        <div
          className="mt-5 h-2 w-full rounded-full overflow-hidden"
          style={{ background: t.progressTrack }}
        >
          <motion.div
            className="h-full rounded-full"
            style={{ background: ACCENT }}
            initial={{ width: 0 }}
            animate={{ width: `${pct}%` }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            data-testid="progress-bar-fill"
          />
        </div>

        {/* Counter chips */}
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
          <Chip label="Total" value={total} tone={t.chipNeutral} testid="chip-total" />
          <Chip label="Completed" value={completed} tone={t.chipGreen} testid="chip-completed" />
          <Chip label="In progress" value={inProgress} tone={t.chipAccent} testid="chip-in-progress" />
          <Chip label="Waiting" value={waiting} tone={t.chipMuted} testid="chip-waiting" />
        </div>
      </div>

      {/* Deliverables list */}
      <div
        className="squircle p-6 sm:p-8"
        style={{ background: t.cardBg, border: `1px solid ${t.cardBorder}`, color: t.text }}
        data-testid="deliverables-list-card"
      >
        <div className="flex items-baseline justify-between gap-4 mb-5">
          <h3 className="text-xl font-semibold tracking-tight" style={{ color: t.text }}>
            Deliverables
          </h3>
          <div className="text-xs" style={{ color: t.textMuted }}>
            Tap any item to view its production timeline
          </div>
        </div>

        <div className="space-y-2.5">
          {items.map((d, i) => (
            <DeliverableRow key={d.id || i} d={d} t={t} />
          ))}
        </div>
      </div>
    </div>
  );
}

function Chip({ label, value, tone, testid }) {
  return (
    <div
      className="squircle-sm px-3 py-2.5 flex items-center justify-between"
      style={{ background: tone.bg, color: tone.text, border: `1px solid ${tone.border}` }}
      data-testid={testid}
    >
      <span className="text-[11px] uppercase tracking-widest opacity-80">{label}</span>
      <span className="text-base font-semibold tabular-nums">{value}</span>
    </div>
  );
}
